/**
 * Desktop Command Dashboard — thin loaders over data the app already reads.
 * No new tables or queries beyond narrow column selects on existing ones;
 * every loader respects the same org scoping / role narrowing as its module.
 */
import { supabase } from "../../supabase";
import { isEmployee } from "../permissions";
import { isOffline } from "../connectivity";
import { shouldSkipSupabase } from "../guestMode";
import { resolveOrganizationIdForQueries } from "../orgQueryScope";
import { loadScopedActiveProjects } from "../operationalQueryAdapters";
import { readCachedFieldProjects } from "../projectFieldSnapshot";
import { readCachedProjectTasks } from "../tasks/tasksCache";
import { isTasksUnavailableError } from "../tasks/projectTasks";
import { canSeeDashboardTasks } from "./dashboardTasks";
import {
  buildAccountsReceivableDashboard,
  isOverdueOpenInvoice,
  loadReceivableInvoices,
} from "../accountsReceivable";
import { loadEstimates, resolveEstimateStatus } from "../estimates";
import { listFinanceAccounts } from "../financeAccounts";
import {
  computeFinancePosition,
  loadExpectedCashInvoices,
  loadPaidTaxInvoices,
  loadPostedMovements,
} from "../financePosition";
import {
  applyOperationalTimeLogOrgScope,
  applyUserIdsToQuery,
  resolveOperationalOrganizationId,
  resolveScopedUserIdsForQuery,
  selectOperationalTimeLogs,
} from "../scopedQueries";
import { CAPABILITY } from "../permissions";
import { buildEstimatePipeline } from "./commandDashboardModel";

const PROJECT_SELECT =
  "project_id, project_name, project_number, status, is_active, client_id, organization_id, created_at, updated_at";
const CHUNK = 60;

function chunk(ids) {
  const out = [];
  for (let i = 0; i < ids.length; i += CHUNK) out.push(ids.slice(i, i + CHUNK));
  return out;
}

/** Active projects + their tasks (all statuses). null when tasks are not available to this user. */
export async function loadCommandTasks(actor) {
  if (!canSeeDashboardTasks(actor) || shouldSkipSupabase(actor)) return null;

  if (isOffline()) {
    const projects = readCachedFieldProjects(actor);
    const tasks = projects.flatMap((p) => readCachedProjectTasks(actor, p.project_id) || []);
    return projects.length ? { projects, tasks, fromDevice: true } : null;
  }

  const projects = await loadScopedActiveProjects(supabase, actor, {}, PROJECT_SELECT, { activeOnly: true });
  const ids = projects.map((p) => p.project_id);
  if (ids.length === 0) return { projects: [], tasks: [], fromDevice: false };
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  if (!organizationId) throw new Error("Organization is required.");
  const me = actor.supabase_user_id || actor.id;

  const tasks = [];
  try {
    for (const part of chunk(ids)) {
      let query = supabase
        .from("project_tasks")
        .select("project_task_id, project_id, status, due_date, is_active, assignee_id")
        .eq("organization_id", organizationId)
        .eq("is_active", true);
      if (isEmployee(actor)) query = query.eq("assignee_id", me);
      const { data, error } = await query.in("project_id", part);
      if (error) throw error;
      tasks.push(...(data || []));
    }
  } catch (error) {
    if (isTasksUnavailableError(error)) return null;
    throw error;
  }
  return { projects, tasks, fromDevice: false };
}

export async function loadCommandEstimates(actor) {
  if (shouldSkipSupabase(actor)) return null;
  const rows = await loadEstimates(actor);
  return buildEstimatePipeline(rows, resolveEstimateStatus);
}

export async function loadCommandReceivables(actor) {
  if (shouldSkipSupabase(actor)) return null;
  const rows = await loadReceivableInvoices(actor);
  const dashboard = buildAccountsReceivableDashboard(rows);
  return {
    outstanding: dashboard.outstandingReceivables,
    openCount: dashboard.invoiceCount,
    overdueCount: rows.filter((invoice) => isOverdueOpenInvoice(invoice)).length,
  };
}

export async function loadCommandFinance(actor) {
  if (shouldSkipSupabase(actor)) return null;
  const [accounts, movements] = await Promise.all([
    listFinanceAccounts(supabase, { includeInactive: true }),
    loadPostedMovements(supabase),
  ]);
  let invoices = [];
  let paidTaxInvoices = [];
  try {
    invoices = await loadExpectedCashInvoices(supabase);
  } catch {
    invoices = [];
  }
  try {
    paidTaxInvoices = await loadPaidTaxInvoices(supabase);
  } catch {
    paidTaxInvoices = [];
  }
  return {
    accounts,
    movements,
    position: computeFinancePosition(accounts, movements, { invoices, paidTaxInvoices }),
  };
}

/** Daily hours for the last `sinceIso`..today, scoped exactly like the dashboard's recent activity. */
export async function loadCommandTimeLogs(actor, { sinceIso, directReportIds }) {
  if (shouldSkipSupabase(actor) || isOffline() || !actor?.supabase_user_id) return null;
  const organizationId = await resolveOperationalOrganizationId(actor, supabase, { directReportIds });
  const userIds = await resolveScopedUserIdsForQuery(supabase, actor, CAPABILITY.TIMELOG_VIEW, {
    directReportIds,
  });
  let query = selectOperationalTimeLogs(supabase, "date_iso, total_hours").gte("date_iso", sinceIso);
  query = applyUserIdsToQuery(query, userIds);
  query = applyOperationalTimeLogOrgScope(query, organizationId);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}
